import { getWallets } from '@wallet-standard/app';
import bs58 from 'bs58';
import { getPlatform } from '../../platform';
// The MWA authorization is CLUSTER-SCOPED, and transactions are signed on the
// app's configured network (resolveSolanaWalletStandardChain). Registering on a
// different chain than the app signs on produces a wallet that authorizes for
// one cluster and is asked to sign for another, so the two must agree - and the
// only chains that can agree are the ones the signing path supports.
const CLUSTER_CHAINS = {
    'mainnet-beta': 'solana:mainnet',
    devnet: 'solana:devnet',
};
/**
 * The app's own configuration is wrong (a network we cannot map, a cluster that
 * contradicts it, a page already registered for another chain). Distinct from an
 * availability failure because it degrades differently: a wallet that simply is
 * not there leaves the lane working for every other wallet, while this is a
 * developer bug that must stay loud instead of quietly removing the lane.
 */
export class WalletConfigError extends Error {
    constructor(message) {
        super(message);
        this.name = 'WalletConfigError';
    }
}
/** Wallet Standard chain id the MWA wallet is registered for.
 *
 *  `network` is the caller's already-resolved authoritative Solana network
 *  (getConfiguredSolanaNetwork: the wallet-lane override, else config.chain) -
 *  the SAME value the provider signs and submits on. It is passed in rather
 *  than re-derived here so registration can never disagree with signing, and so
 *  this module stays out of the auth barrel's import cycle.
 *  `mobileWalletConfig.cluster` may name the chain explicitly (the only way a
 *  chainless, login-only app can authorize off mainnet) but may never
 *  contradict it. A chainless app with no cluster defaults to mainnet. Throws on
 *  a contradiction or an unsupported cluster: that is the app developer's bug,
 *  and a silent mismatch would only surface as a wallet rejection at signing
 *  time. */
function mwaChain(config, network) {
    var _a;
    const fromNetwork = network === 'solana_mainnet'
        ? 'solana:mainnet'
        : network === 'solana_devnet'
            ? 'solana:devnet'
            : null;
    // A network the mapping above does not recognise must NEVER fall through to
    // the chainless default: registering mainnet for an app configured with,
    // say, solana_testnet authorizes a cluster it will never transact on. Only
    // the genuine absence of a network takes the default.
    if (network != null && network !== '' && fromNetwork === null) {
        throw new WalletConfigError(`Bounded: the Solana Mobile wallet cannot be registered for network "${network}". ` +
            `Expected one of: solana_devnet, solana_mainnet.`);
    }
    const cluster = (_a = config.mobileWalletConfig) === null || _a === void 0 ? void 0 : _a.cluster;
    if (cluster == null)
        return fromNetwork !== null && fromNetwork !== void 0 ? fromNetwork : 'solana:mainnet';
    const fromCluster = CLUSTER_CHAINS[cluster];
    if (!fromCluster) {
        throw new WalletConfigError(`Bounded: mobileWalletConfig.cluster "${cluster}" is not supported. ` +
            `Use "mainnet-beta" or "devnet".`);
    }
    if (fromNetwork && fromNetwork !== fromCluster) {
        throw new WalletConfigError(`Bounded: mobileWalletConfig.cluster "${cluster}" contradicts the app's Solana network ` +
            `"${network}". The mobile wallet authorizes per cluster and signs on that network, so ` +
            `they must match - drop the cluster override, or set the network you actually mean.`);
    }
    return fromCluster;
}
/** Per the MWA spec `appIdentity.icon` is a path the wallet resolves RELATIVE to
 *  `appIdentity.uri`. Resolve whatever the app configured (usually
 *  config.logoUrl) and hand back a path that round-trips: stripping the leading
 *  slash is only correct when `uri` is a bare origin, and silently pointed at
 *  `<uri-dir>/icon.svg` for any `uri` carrying a path. A cross-origin icon
 *  cannot be expressed as a relative path at all and is dropped. */
function relativeAppIcon(icon, uri) {
    if (!icon)
        return undefined;
    try {
        const base = new URL(uri);
        const resolved = new URL(icon, base);
        if (resolved.origin !== base.origin)
            return undefined;
        // Walk from the uri's DIRECTORY (a uri with no trailing slash resolves
        // relative to its parent, exactly as the wallet will) to the icon.
        const from = base.pathname.replace(/[^/]*$/, '').split('/').filter(Boolean);
        const to = resolved.pathname.split('/').filter(Boolean);
        let shared = 0;
        while (shared < from.length && shared < to.length && from[shared] === to[shared])
            shared += 1;
        const up = '../'.repeat(from.length - shared);
        return up + to.slice(shared).join('/') + resolved.search;
    }
    catch (_a) {
        return undefined;
    }
}
/**
 * The transaction features a wallet's OWN reported capabilities allow.
 *
 * Strictly, on POSITIVE evidence only. The library exposes both transaction
 * features optimistically at the wallet level - including a fallback that offers
 * sign-and-send to a wallet reporting neither - but that exposure is not the
 * executable contract: both transports re-read the live capabilities inside
 * signAndSendTransaction and throw before touching the wallet unless
 * `supports_sign_and_send_transactions` is true. Mirroring the optimistic
 * exposure would therefore only route a doomed call down the native branch
 * instead of Bounded's sign-then-submit fallback.
 */
function walletAllowedTxFeatures(capabilities) {
    return {
        signAndSend: (capabilities === null || capabilities === void 0 ? void 0 : capabilities.supports_sign_and_send_transactions) === true,
        sign: Array.isArray(capabilities === null || capabilities === void 0 ? void 0 : capabilities.features)
            ? capabilities.features.includes('solana:signTransactions')
            : false,
    };
}
/**
 * A persisted MWA authorization, scoped to the chain (and app identity) it was
 * granted for.
 *
 * The library's own cache keeps ONE record under one localStorage key and hands
 * it back without comparing chains, so a mainnet authorization cached earlier
 * is reused by a devnet-configured page later: the wallet then advertises
 * devnet while holding a mainnet authorization, and its native send follows the
 * authorization rather than the app. localStorage survives a reload, so
 * "reload the page" cannot clear it either. Keying the record by chain and app
 * identity makes that mismatch unrepresentable instead of silently signing on
 * the wrong cluster.
 */
function chainScopedAuthorizationCache(chain, identityUri) {
    const key = `bounded:mwa-authorization:${identityUri}:${chain}`;
    const storage = () => {
        try {
            return window.localStorage;
        }
        catch (_a) {
            return null;
        }
    };
    return {
        async get() {
            var _a;
            try {
                const raw = (_a = storage()) === null || _a === void 0 ? void 0 : _a.getItem(key);
                if (!raw)
                    return undefined;
                const parsed = JSON.parse(raw);
                // Belt and braces: refuse a record that does not name our chain,
                // however it came to be stored under this key.
                if ((parsed === null || parsed === void 0 ? void 0 : parsed.chain) !== chain)
                    return undefined;
                if (!Array.isArray(parsed.accounts))
                    return parsed;
                // REVIVE the account keys. JSON has no Uint8Array, so a plain
                // round trip hands the wallet a `{0:..,1:..}` object and the
                // address it derives from it comes out empty. This mirrors the
                // upstream cache's revival exactly - only the key and the chain
                // check are ours.
                // RECONCILE the account capabilities against what the wallet
                // itself reported, which this record also carries. An
                // authorization that named no per-account features was stored
                // with the library's optimistic defaults - and those include
                // sign-and-send. The library narrows its wallet-level features
                // from the cached capabilities on its SILENT path only; an
                // interactive connect that hits this cache (a returning user on
                // a fresh page) leaves them at the constructor's optimistic
                // pair. The account would then be the only thing standing
                // between a sign-only wallet and the native-send branch - and it
                // would wave it through, so the wallet rejects the call instead
                // of Bounded falling back to sign-then-submit.
                // A record with accounts but no capabilities cannot be judged at
                // all, and guessing is what this exists to stop. Drop it: the
                // library then re-authorizes once and stores a complete record,
                // so it self-heals instead of overstating the wallet forever.
                if (!parsed.capabilities)
                    return undefined;
                const allowed = walletAllowedTxFeatures(parsed.capabilities);
                const permitted = (feature) => (feature === 'solana:signAndSendTransaction' ? allowed.signAndSend
                    : feature === 'solana:signTransaction' ? allowed.sign
                        : true);
                return Object.assign(Object.assign({}, parsed), { accounts: parsed.accounts.map((account) => (Object.assign(Object.assign(Object.assign({}, account), { publicKey: 'publicKey' in account
                            ? new Uint8Array(Object.values(account.publicKey))
                            : bs58.decode(account.address) }), (Array.isArray(account.features)
                        ? { features: account.features.filter(permitted) }
                        : {})))) });
            }
            catch (_b) {
                return undefined;
            }
        },
        async set(authorization) {
            var _a;
            try {
                if ((authorization === null || authorization === void 0 ? void 0 : authorization.chain) !== chain)
                    return;
                (_a = storage()) === null || _a === void 0 ? void 0 : _a.setItem(key, JSON.stringify(authorization));
            }
            catch ( /* private mode / quota: an uncached authorization just re-prompts */_b) { /* private mode / quota: an uncached authorization just re-prompts */ }
        },
        async clear() {
            var _a;
            try {
                (_a = storage()) === null || _a === void 0 ? void 0 : _a.removeItem(key);
            }
            catch ( /* nothing to clear */_b) { /* nothing to clear */ }
        },
    };
}
// One attempt per distinct registration input. A no-op attempt (an ordinary
// desktop browser registers nothing) is memoized only for the input that
// produced it, so later adding a remoteHostAuthority - which enables the remote
// QR wallet - gets a real second attempt instead of the first one's silence.
const attempts = new Map();
// The chain of the wallet that ACTUALLY registered, and of an attempt still in
// flight. Both block a conflicting chain: once a wallet exists it cannot be
// moved, and while one is being created the outcome is already decided.
let registeredChain = null;
let pendingChain = null;
// The material configuration of an attempt still in flight. Chain alone is not
// enough: two concurrent same-chain calls with different reflectors would both
// pass a chain-only check and register two adapters that can never be withdrawn.
let pendingConfig = null;
// The exact effective configuration a registration SUCCEEDED with. A wallet in
// the registry cannot be withdrawn, so once this is set any material change is
// refused rather than quietly registering a second, unreachable adapter beside
// the first (discovery would keep serving the original).
let registeredConfig = null;
// The wallet OBJECTS registerMwa() actually put in the registry. Identity, not
// name: a display name is a label anyone can reuse (another SDK on the page can
// register its own "Mobile Wallet Adapter"), and mistaking someone else's wallet
// for ours - or failing to recognise our own - decides whether a fresh gesture
// is collected. Weak so a wallet that goes away is not retained here.
const mobileWallets = new WeakSet();
// The NAMES the MWA package registers under, captured from its own exports.
// Identity answers "is this the wallet we registered"; this answers a different
// question - "is this an MWA-transport wallet at all", including one another SDK
// registered. A label is exactly what a lookalike copies, so it is the only
// signal available there, and being wrong only costs a duplicate chooser entry
// rather than hiding a wallet the user has installed.
// The names the Mobile Wallet Adapter package registers under. They are the
// library's own exported constants, restated here because the answer is needed
// BEFORE (and without) loading that chunk - a page that never opts into wallet
// login must still be able to tell that a wallet another SDK registered speaks
// this transport. Registration re-adds whatever the installed library actually
// exports, so an upstream rename degrades to "we also know the new name" rather
// than to a wrong answer.
const LOCAL_MOBILE_WALLET_NAME = 'Mobile Wallet Adapter';
const mobileWalletNames = new Set([
    LOCAL_MOBILE_WALLET_NAME,
    'Remote Mobile Wallet Adapter',
]);
/**
 * Whether a discovered wallet is the Solana Mobile one this module registered.
 *
 * Callers need it because that wallet is the only one that LEAVES THE PAGE to
 * sign - it needs a fresh user gesture per operation (see
 * InjectedWalletConfig.confirmWalletAction). False before registration, for
 * every injected wallet, and for a lookalike another SDK registered.
 */
export function isSolanaMobileWallet(wallet) {
    return !!wallet && mobileWallets.has(wallet);
}
/**
 * Whether a registered wallet reaches its signer through the Mobile Wallet
 * Adapter transport - ours OR a lookalike another SDK registered. Used to decide
 * whether the registry already covers the IN-PAGE wallets, which is a question
 * about transport, not ownership.
 */
export function looksLikeSolanaMobileWallet(name) {
    return mobileWalletNames.has(name);
}
/**
 * Whether reaching this wallet LEAVES THE PAGE, which is what makes a fresh
 * user gesture necessary. True for the local (on-device) transport, which
 * dispatches an Android intent per operation. NOT true for the remote QR
 * transport: it keeps its websocket session and drives an in-page modal, so an
 * extra "one more tap" screen there would be friction for nothing.
 */
export function solanaMobileWalletLeavesPage(name) {
    return name === LOCAL_MOBILE_WALLET_NAME;
}
/**
 * Register Solana Mobile's MWA wallet with the Wallet Standard registry so
 * wallet discovery surfaces it. Idempotent - the first wallet-lane activation
 * (init with a wallet authMethod, loginWithWallet, or the login widget) wins
 * for the page's lifetime.
 *
 * Rejects on a contradictory/unsupported config, and on a later activation that
 * needs a DIFFERENT chain than the one already registered: the MWA
 * authorization is cluster-scoped and `registerMwa` hands back no unregister
 * handle, so the wallet in the registry cannot be moved. Failing loudly beats
 * leaving a wallet that authorizes on one cluster and is asked to sign on
 * another - and the app's own fix (reload before switching networks) is one the
 * error can state. A registration or environment failure resolves after
 * warning: it degrades to "MWA not listed" rather than breaking the injected
 * wallets sharing the lane.
 */
export async function ensureSolanaMobileWalletRegistered(config, network) {
    var _a, _b, _c, _d, _e;
    // React Native exposes a partial `window` but has no DOM; wallet-standard
    // registration is meaningless there (RN uses the MWA protocol directly via
    // its own providers). registerMwa also requires window, but gate here so
    // the chunk is never even fetched off-web.
    // Off-web there is nothing to register and nothing to wait for.
    if (typeof window === 'undefined' || !getPlatform().hasDOM)
        return 'not-applicable';
    // Resolve the chain BEFORE the memo so a contradictory config rejects to the
    // caller. Config errors are the app developer's bug and must be loud; only
    // the environment/registration failures inside the memo degrade to a warning.
    const chain = mwaChain(config, network);
    const identity = (_b = (_a = config.mobileWalletConfig) === null || _a === void 0 ? void 0 : _a.appIdentity) !== null && _b !== void 0 ? _b : {};
    const uri = (_c = identity.uri) !== null && _c !== void 0 ? _c : window.location.origin;
    // What a registered wallet CANNOT change. The chain scopes its authorization;
    // the reflector authority decides whether registerMwa creates the local or
    // the remote (QR) wallet, so a different one means a different wallet object
    // - and since the registration cannot be withdrawn, registering it anyway
    // would leave a second adapter that discovery never serves. The app identity
    // is deliberately NOT here: it is what the wallet displays, so a page that
    // re-inits with a new name keeps the first label rather than losing login
    // over a cosmetic difference.
    const effective = JSON.stringify({
        chain,
        remoteHostAuthority: (_e = (_d = config.mobileWalletConfig) === null || _d === void 0 ? void 0 : _d.remoteHostAuthority) !== null && _e !== void 0 ? _e : null,
    });
    // A wallet already in (or entering) the registry cannot be withdrawn, so
    // anything material that differs means this page is asking for a wallet it
    // cannot have. Check the chain first: it is the common case and deserves the
    // specific message.
    const committedChain = registeredChain !== null && registeredChain !== void 0 ? registeredChain : pendingChain;
    if (committedChain && committedChain !== chain) {
        throw new WalletConfigError(`Bounded: the Solana Mobile wallet is already registered for ${committedChain} and ` +
            `cannot be moved to ${chain} on a live page (its authorization is cluster-scoped and ` +
            `the Wallet Standard registration cannot be withdrawn). Reload the page after ` +
            `changing the app's Solana network.`);
    }
    const committed = registeredConfig !== null && registeredConfig !== void 0 ? registeredConfig : pendingConfig;
    if (committed && committed !== effective) {
        throw new WalletConfigError(`Bounded: the Solana Mobile wallet is already registered for this page ` +
            `(${committedChain !== null && committedChain !== void 0 ? committedChain : chain}) and its registration cannot be withdrawn, so its ` +
            `network and reflector are fixed for the page's lifetime. Reload after changing ` +
            `the app's Solana network or mobileWalletConfig.remoteHostAuthority.`);
    }
    const attemptKey = effective;
    const inFlight = attempts.get(attemptKey);
    if (!inFlight) {
        pendingChain = chain;
        pendingConfig = effective;
        const run = (async () => {
            var _a, _b, _c, _d;
            // Optional peer dependency, loaded only on the one path that needs a
            // mobile wallet. Keeping it OFF the hard dependency list keeps
            // react-native (and the whole metro toolchain underneath it) out of
            // every web consumer's install; a missing module here is a config
            // problem in the consuming app, not a runtime fault, so name it as
            // one instead of surfacing a bare MODULE_NOT_FOUND.
            let mwa;
            try {
                mwa = await import('@solana-mobile/wallet-standard-mobile');
            }
            catch (_e) {
                throw new WalletConfigError(`Bounded: Solana Mobile wallet support needs the optional peer dependency ` +
                    `'@solana-mobile/wallet-standard-mobile', which is not installed. Install it ` +
                    `in your app to enable the mobile wallet, or leave the Solana Mobile wallet ` +
                    `out of this app's wallet configuration.`);
            }
            const { registerMwa, createDefaultChainSelector, SolanaMobileWalletAdapterWalletName, SolanaMobileWalletAdapterRemoteWalletName, } = mwa;
            const before = new Set(getWallets().get());
            registerMwa({
                appIdentity: {
                    uri,
                    name: (_b = (_a = identity.name) !== null && _a !== void 0 ? _a : config.name) !== null && _b !== void 0 ? _b : (document.title || 'Bounded App'),
                    icon: relativeAppIcon((_c = identity.icon) !== null && _c !== void 0 ? _c : config.logoUrl, uri),
                },
                authorizationCache: chainScopedAuthorizationCache(chain, uri),
                chains: [chain],
                chainSelector: createDefaultChainSelector(),
                remoteHostAuthority: (_d = config.mobileWalletConfig) === null || _d === void 0 ? void 0 : _d.remoteHostAuthority,
                // Suppress the library's "we can't find a wallet" modal: it is
                // misleading when ERROR_WALLET_NOT_FOUND fires during Seeker's
                // first-time Local-Network-Access consent race (the wallet IS
                // installed), and Bounded's own login surface already shows the
                // failure inline in its UI.
                onWalletNotFound: async () => { },
            });
            // registerMwa decides per environment and REGISTERS NOTHING on an
            // ordinary desktop browser (no Android, no remote authority).
            // Claim only the wallet OBJECTS this call added, and only when they
            // carry a name the library itself exports: someone else's wallet
            // sharing that label is not ours, and adopting it would record a
            // chain we never registered.
            const mwaNames = new Set([
                SolanaMobileWalletAdapterWalletName,
                SolanaMobileWalletAdapterRemoteWalletName,
            ]);
            for (const name of mwaNames)
                mobileWalletNames.add(name);
            let added = false;
            for (const wallet of getWallets().get()) {
                if (before.has(wallet) || !mwaNames.has(wallet.name))
                    continue;
                mobileWallets.add(wallet);
                added = true;
            }
            // No wallet materialized (the ordinary desktop path): claim no
            // chain, so a later network change is not rejected as though an
            // immovable wallet existed.
            if (!added)
                return 'not-applicable';
            registeredChain = chain;
            registeredConfig = effective;
            return 'registered';
        })().catch((error) => {
            console.warn('[Bounded] Solana Mobile wallet registration failed:', error);
            // A thrown failure (chunk load, wallet-standard hiccup) may be
            // transient, so drop this attempt and let a later activation retry.
            attempts.delete(attemptKey);
            return 'failed';
        }).finally(() => {
            pendingChain = null;
            pendingConfig = null;
        });
        attempts.set(attemptKey, run);
        return run;
    }
    return inFlight;
}
